/**
 * La zone où l'on dépose la liste officielle : on y glisse le fichier, ou on
 * le choisit. Le simple bouton « Choisir un fichier » d'avant se perdait dans
 * la page (recette de Daniel, 01/11/2026).
 *
 * Trois états : vide (zone de dépôt en pointillés), lecture en cours, fichier
 * lu (une carte avec son format, son nom et ses lignes, et « Changer de
 * fichier »). Un fichier glissé sur la carte remplace le précédent.
 */
import React, { useRef, useState } from 'react';
import { FileUp, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { FORMATS_ACCEPTES } from './lectureListeOfficielle';

const ZoneFichier = ({ fichier, lignesLues = 0, lecture = false, onChoisir }) => {
  const champ = useRef(null);
  const [survol, setSurvol] = useState(false);

  const ouvrir = () => champ.current?.click();

  const deposer = (e) => {
    e.preventDefault();
    setSurvol(false);
    const leFichier = e.dataTransfer?.files?.[0];
    if (leFichier && !lecture) onChoisir(leFichier);
  };

  const evenementsDepot = {
    onDragOver: (e) => {
      e.preventDefault();
      if (!survol) setSurvol(true);
    },
    onDragLeave: () => setSurvol(false),
    onDrop: deposer
  };

  const champCache = (
    <input
      ref={champ}
      type="file"
      accept={FORMATS_ACCEPTES}
      className="hidden"
      data-testid="champ-fichier"
      onChange={(e) => {
        const leFichier = e.target.files?.[0];
        if (leFichier) onChoisir(leFichier);
        // Rechoisir le même fichier doit relancer la lecture.
        e.target.value = '';
      }}
    />
  );

  if (lecture) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-lg border-2 border-dashed border-[#246990]/40 bg-[#eef7fb] px-6 py-10 text-sm text-[#246990]">
        <Loader2 size={18} className="animate-spin" />
        Lecture du fichier…
      </div>
    );
  }

  if (fichier) {
    return (
      <div
        {...evenementsDepot}
        className={`flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4 transition-colors ${survol
          ? 'border-[#246990] bg-[#eef7fb]'
          : 'border-gray-200 bg-white'
          }`}
      >
        {champCache}
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-xs font-bold uppercase tracking-wide text-white">
            {fichier.format}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{fichier.nom}</p>
            <p className="text-xs text-gray-500">
              {lignesLues} ligne{lignesLues > 1 ? 's' : ''} lue{lignesLues > 1 ? 's' : ''}
              {' - '}le fichier reste sur cet ordinateur
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={ouvrir}
          className="h-9 rounded-lg border-[#246990] px-4 text-sm text-[#246990] hover:bg-[#246990] hover:text-white"
        >
          Changer de fichier
        </Button>
      </div>
    );
  }

  return (
    <div
      {...evenementsDepot}
      onClick={ouvrir}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          ouvrir();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Déposer ou choisir la liste officielle"
      className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#246990]/30 ${survol
        ? 'border-[#246990] bg-[#eef7fb]'
        : 'border-gray-300 bg-gray-50 hover:border-[#246990]/60 hover:bg-[#eef7fb]/60'
        }`}
    >
      {champCache}
      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#246990] text-white">
        <FileUp size={22} />
      </span>
      <div>
        <p className="text-sm font-semibold text-gray-900">
          {survol ? 'Lâchez le fichier ici' : 'Glissez la liste officielle ici'}
        </p>
        <p className="mt-1 text-xs text-gray-500">ou</p>
      </div>
      <Button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          ouvrir();
        }}
        className="h-9 rounded-lg bg-[#246990] px-4 text-sm text-white hover:bg-[#1f5a7a]"
      >
        Choisir un fichier
      </Button>
      <p className="text-xs text-gray-500">
        Excel (.xlsx, .xls), ODS, CSV ou PDF. Le fichier est lu sur cet ordinateur, il n&apos;est envoyé nulle part.
      </p>
    </div>
  );
};

export default ZoneFichier;
